import { Color3, InstancedMesh, Mesh, MeshBuilder, PBRMaterial, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

interface LaserRow {
  laserInst: InstancedMesh;
  collisionInst: InstancedMesh;
  active: boolean;
  phaseOffset: number;
}

/**
 * Laser Grid Behavior – horizontal laser rows blinking on/off in a wave pattern.
 * Beams are oriented laterally to the path (+90° around Y).
 *
 * Uses instanced meshes: one shared source beam + one shared source collision box,
 * created once per game session.
 *
 * Config fields:
 *   laserRows      – number of rows (default 4)
 *   height         – vertical spacing between rows (default 1.5)
 *   width          – laser beam length (default 6)
 *   fireInterval   – half-cycle duration in seconds (default 0.6)
 *   speed          – wave stagger multiplier (default 1)
 */
export class ObstacleLaserGridBehavior extends ObstacleBehavior {
  /** Shared source meshes – never rendered, isVisible = false */
  static sourceLaser: Mesh;
  static sourceCollision: Mesh;
  static laserMat: PBRMaterial;

  private time: number = 0;
  private rows: LaserRow[] = [];

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleLaserGrid', startPosition, pathIndex, config);
  }

  static dispose(): void {
    if (ObstacleLaserGridBehavior.sourceLaser) {
      ObstacleLaserGridBehavior.sourceLaser.dispose();
      ObstacleLaserGridBehavior.sourceLaser = null;
    }
    if (ObstacleLaserGridBehavior.sourceCollision) {
      ObstacleLaserGridBehavior.sourceCollision.dispose();
      ObstacleLaserGridBehavior.sourceCollision = null;
    }
    if (ObstacleLaserGridBehavior.laserMat) {
      ObstacleLaserGridBehavior.laserMat.dispose();
      ObstacleLaserGridBehavior.laserMat = null;
    }
  }

  initLevel(level: Level): ObstacleLaserGridBehavior {
    super.initLevel(level);

    const rowCount = this.config.laserRows ?? 4;
    const rowSpacing = this.config.height ?? 0.5;
    const beamLength = this.config.width ?? 6;
    const yOffset = this.mesh.getBoundingInfo().boundingBox.extendSizeWorld.y / 2;
    // ── Shared material ───────────────────────────────────────────────────────
    if (!ObstacleLaserGridBehavior.laserMat) {
      const mat = this.level.game.materialService.createPBRColorMaterial('laserGridMat', new Color3(1, 0, 0), 0.6);
      mat.metallic = 0.9;
      mat.roughness = 0.1;
      ObstacleLaserGridBehavior.laserMat = mat;
    }

    // ── Shared source meshes ──────────────────────────────────────────────────
    if (!ObstacleLaserGridBehavior.sourceLaser) {
      const src = MeshBuilder.CreateBox('laserGrid_source', { width: beamLength, height: 0.05, depth: 0.05 }, this.level.scene);
      src.material = ObstacleLaserGridBehavior.laserMat;
      src.isVisible = false;
      src.isPickable = false;
      ObstacleLaserGridBehavior.sourceLaser = src;
    }

    if (!ObstacleLaserGridBehavior.sourceCollision) {
      const srcCol = MeshBuilder.CreateBox('laserGrid_sourceCol', { width: beamLength, height: 0.25, depth: 0.25 }, this.level.scene);
      srcCol.isVisible = false;
      srcCol.isPickable = false;
      srcCol.rotation.y = Math.PI / 2;
      ObstacleLaserGridBehavior.sourceCollision = srcCol;
    }

    // ── Spawn instances per row ───────────────────────────────────────────────
    for (let i = 0; i < rowCount; i++) {
      const yPos = i * rowSpacing + yOffset;
      const phaseOffset = i / rowCount; // stagger each row

      const laserInst = ObstacleLaserGridBehavior.sourceLaser.createInstance(`laserRow_${i}`);
      laserInst.parent = this.mesh;
      laserInst.position.y = yPos;
      laserInst.isPickable = false;
      laserInst.isVisible = false; // start off, toggled in update()

      const collisionInst = ObstacleLaserGridBehavior.sourceCollision.createInstance(`laserRowCol_${i}`);
      collisionInst.parent = this.mesh;
      collisionInst.position.y = yPos;
      collisionInst.isVisible = false;
      collisionInst.isPickable = false;

      collisionInst.metadata = {
        playerCollision: new PlayerCollision(false, (level, player, event) => {
          if (player.vulnerable && !level.game.config.godMode) {
            player.collide(event, 'Hit by laser!');
          }
        }),
      };

      this.rows.push({ laserInst, collisionInst, active: false, phaseOffset });
    }

    return this;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;
    const halfCycle = this.config.fireInterval ?? 0.6;
    const speed = this.config.speed ?? 1;

    for (const row of this.rows) {
      const t = (this.time * speed + row.phaseOffset) % (halfCycle * 2);
      const shouldBeActive = t < halfCycle;

      if (shouldBeActive !== row.active) {
        row.active = shouldBeActive;
        row.laserInst.isVisible = shouldBeActive;

        if (shouldBeActive) {
          this.level?.player?.registerCollisionMesh(row.collisionInst);
        } else {
          this.level?.player?.unregisterCollisionMesh(row.collisionInst);
        }
      }
    }
  }

  detach(): void {
    for (const row of this.rows) {
      this.level?.player?.unregisterCollisionMesh(row.collisionInst);
      row.laserInst.dispose();
      row.collisionInst.dispose();
    }
    this.rows = [];
    super.detach();
  }

  clone(): ObstacleLaserGridBehavior {
    return new ObstacleLaserGridBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
